import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface InvalidationMessage {
  type: 'invalidate';
  eventType: 'product' | 'inventory' | 'collection' | 'price' | 'blog' | 'general';
  topic: string;
  handles: string[];
  ids: string[];
  timestamp: number;
}

/**
 * Hook to listen for Shopify sync events via Supabase Realtime.
 * When a webhook is received, it broadcasts an invalidation message
 * that triggers React Query cache invalidation.
 */
export function useShopifySync() {
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  
  const handleInvalidation = useCallback((message: InvalidationMessage) => {
    console.log('[ShopifySync] Received invalidation:', message);
    
    const { eventType, handles, ids } = message;
    
    // Invalidate based on event type
    switch (eventType) {
      case 'product':
      case 'inventory':
      case 'price':
        // Invalidate all product-related queries
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['all-products'] });
        queryClient.invalidateQueries({ queryKey: ['single-products'] });
        queryClient.invalidateQueries({ queryKey: ['bundles'] });
        queryClient.invalidateQueries({ queryKey: ['all-vapes'] });
        queryClient.invalidateQueries({ queryKey: ['buds-hash'] });
        
        // Invalidate specific product pages by handle
        handles.forEach(handle => {
          queryClient.invalidateQueries({ queryKey: ['product', handle] });
        });
        break;
        
      case 'collection':
        // Invalidate collection-related queries
        queryClient.invalidateQueries({ queryKey: ['collection'] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
        break;
        
      case 'blog':
        // Invalidate blog-related queries
        queryClient.invalidateQueries({ queryKey: ['shopify-blog-articles'] });
        queryClient.invalidateQueries({ queryKey: ['shopify-article'] });
        break;
        
      case 'general':
      default:
        // Invalidate everything
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['product'] });
        queryClient.invalidateQueries({ queryKey: ['all-products'] });
        queryClient.invalidateQueries({ queryKey: ['single-products'] });
        queryClient.invalidateQueries({ queryKey: ['bundles'] });
        queryClient.invalidateQueries({ queryKey: ['all-vapes'] });
        queryClient.invalidateQueries({ queryKey: ['buds-hash'] });
        queryClient.invalidateQueries({ queryKey: ['collection'] });
        queryClient.invalidateQueries({ queryKey: ['shopify-blog-articles'] });
        queryClient.invalidateQueries({ queryKey: ['shopify-article'] });
        break;
    }
    
    console.log('[ShopifySync] Cache invalidated for eventType:', eventType);
  }, [queryClient]);
  
  useEffect(() => {
    // Defer WebSocket connection until user interacts with page
    // This prevents ERR_NAME_NOT_RESOLVED errors on initial load
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let connected = false;
    
    const connect = () => {
      if (connected) return;
      connected = true;
      
      channel = supabase.channel('shopify-sync')
        .on('broadcast', { event: 'invalidate' }, ({ payload }) => {
          if (payload && payload.type === 'invalidate') {
            handleInvalidation(payload as InvalidationMessage);
          }
        })
        .subscribe();
      
      channelRef.current = channel;
    };
    
    // Connect on first user interaction (click, scroll, focus)
    const events = ['click', 'scroll', 'keydown', 'touchstart'];
    const handler = () => {
      connect();
      events.forEach(e => window.removeEventListener(e, handler));
    };
    events.forEach(e => window.addEventListener(e, handler, { once: true, passive: true }));
    
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [handleInvalidation]);
  
  // Manual refresh function for users
  const forceRefresh = useCallback(() => {
    console.log('[ShopifySync] Manual refresh triggered');
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['product'] });
    queryClient.invalidateQueries({ queryKey: ['all-products'] });
    queryClient.invalidateQueries({ queryKey: ['single-products'] });
    queryClient.invalidateQueries({ queryKey: ['bundles'] });
    queryClient.invalidateQueries({ queryKey: ['all-vapes'] });
    queryClient.invalidateQueries({ queryKey: ['buds-hash'] });
    queryClient.invalidateQueries({ queryKey: ['shopify-blog-articles'] });
    queryClient.invalidateQueries({ queryKey: ['shopify-article'] });
  }, [queryClient]);
  
  return { forceRefresh };
}
